import * as z from 'zod';
import { individualCreditReportVendorSchema, individualCreditReportFailureReasonSchema } from '../../enums.mjs';
import '../../types/actor/actor.mjs';
import '../../types/address/address.mjs';
import '../../types/credit-report/company-credit-report.mjs';
import { Vendor as Vendor$1, OFAC as OFAC$1 } from '../../types/credit-report/individual-credit-report.mjs';
import '../../types/currency/currency.mjs';
import '../../types/entityRelation/entity-relation.mjs';
import '../../types/financial-formula/adjustable-value.mjs';
import '../../types/financial-formula/adjustable-values-update.mjs';
import '../../types/financial-formula/adjustment.mjs';
import '../../types/financial-formula/formula.mjs';
import '@inrev/core';
import '../../types/is-encrypted.mjs';
import '../../types/naics-codes/naics-codes.mjs';
import { isoDateSchema, fico, isoDateTimeSchema, individualContactId, individualCreditReportId, individualOFACCheckId } from '../../types/opaque.mjs';
import '../../types/surety/account/requested-limits.mjs';
import '../../types/surety/account/surety-account.types.mjs';
import '../../types/surety/application-extraction/application-extraction-output.mjs';
import '../../types/surety/application-extraction/application-extraction-webhook.mjs';
import '../../types/surety/application-extraction/application-review.mjs';
import '../../types/surety/bond/surety-bond.types.mjs';
import '../../types/surety/bond-form/issued/issued-surety-bond-form.types.mjs';
import '../../types/surety/bond-form/surety-bond-form.data.mjs';
import '../../types/surety/bond-request/bond-request-extraction-output.mjs';
import '../../types/surety/bond-request/bond-request-extraction-webhook.mjs';
import '../../types/surety/contract/contract-extraction-output.mjs';
import '../../types/surety/contract/contract-extraction-webhook.mjs';
import '../../types/surety/financial-statement/financial-period/balance-sheet-data.mjs';
import '../../types/surety/financial-statement/financial-period/financial-period-extraction-webhook.mjs';
import '../../types/surety/financial-statement/financial-period/income-statement-data.mjs';
import '../../types/surety/financial-statement/financial-period/income-statement-fields.mjs';
import '../../types/surety/financial-statement/financial-period/income-statement-with-adjustments.mjs';
import '../../types/surety/financial-statement/financial-period/line-item-group.mjs';
import '../../types/surety/financial-statement/financial-period/section-keys.mjs';
import '../../types/surety/financial-statement/financial-period/statement-metadata.mjs';
import '../../types/surety/financial-statement/financial-period/view-shapes.mjs';
import '../../types/surety/financial-statement/financial-statement-schemas.mjs';
import '../../types/surety/indemnity/indemnity-agreement.mjs';
import '../../types/surety/quote/surety-quote.types.mjs';
import '../../types/surety/wip-schedule/wip-schedule-data.mjs';
import '../../types/surety/wip-schedule/wip-schedule-extraction-webhook.mjs';
import '../../types/surety/work-in-progress-statement/surety-work-in-progress-statement.mjs';
import '../../types/user/user.types.mjs';
import { currencySchema } from '../../types/utils.mjs';

var Vendor;
(function (Vendor) {
    const vendorMetaSchema = {
        vendor: individualCreditReportVendorSchema,
        experianCreditProfile: Vendor$1.experianCreditProfileWriteSchema.nullish(),
    };
    Vendor.responseSchema = z.union([
        z.object({
            success: z.literal(false),
            error: Vendor$1.vendorErrorSchema.optional(),
            ...vendorMetaSchema,
        }),
        z.object({
            success: z.literal(true),
            fileFound: z.literal(false),
            ...vendorMetaSchema,
        }),
        z.object({
            success: z.literal(true),
            fileFound: z.literal(true),
            report: Vendor$1.reportSchema,
            ...vendorMetaSchema,
        }),
    ]);
})(Vendor || (Vendor = {}));
var Admin;
(function (Admin) {
    let Experian;
    (function (Experian) {
        Experian.tradelineCategorySchema = z.enum([
            "revolving",
            "installment",
            "mortgage",
            "collection",
            "other",
        ]);
        Experian.paymentMonthSchema = z.enum([
            "on_time",
            "late_30",
            "late_60",
            "late_90_plus",
            "derogatory",
            "no_data",
        ]);
        Experian.scoreFactorSchema = z.object({
            code: z.string(),
            description: z.string().optional(),
        });
        Experian.bankruptcySchema = z.object({
            chapter: z.string().optional(),
            filing: z.enum(["voluntary", "involuntary"]).optional(),
            filedOn: isoDateSchema.optional(),
            status: z.string().optional(),
            statusDate: isoDateSchema.optional(),
            liabilities: currencySchema.optional(),
            assets: currencySchema.optional(),
            court: z.string().optional(),
            docket: z.string().optional(),
            disputed: z.boolean(),
        });
        Experian.lienSchema = z.object({
            amount: currencySchema.optional(),
            filedOn: isoDateSchema.optional(),
            status: z.string().optional(),
            statusDate: isoDateSchema.optional(),
            court: z.string().optional(),
            docket: z.string().optional(),
            disputed: z.boolean(),
        });
        Experian.judgmentSchema = Experian.lienSchema.extend({
            plaintiff: z.string().optional(),
        });
        Experian.collectionSchema = z.object({
            agency: z.string(),
            originalCreditor: z.string().optional(),
            balance: currencySchema.optional(),
            placedOn: isoDateSchema.optional(),
            status: z.string().optional(),
            disputed: z.boolean(),
        });
        Experian.tradelineSchema = z.object({
            creditor: z.string(),
            category: Experian.tradelineCategorySchema,
            accountType: z.string().optional(),
            status: z.enum(["open", "closed", "collection"]),
            statusDescription: z.string().optional(),
            statusDate: isoDateSchema.optional(),
            openedOn: isoDateSchema.optional(),
            balance: currencySchema.optional(),
            limit: z
                .object({
                kind: z.enum(["limit", "high_credit", "original"]),
                amount: currencySchema,
            })
                .optional(),
            pastDue: currencySchema.optional(),
            monthlyPayment: currencySchema.optional(),
            originalCreditor: z.string().optional(),
            paymentHistory: z.array(Experian.paymentMonthSchema),
            paymentHistoryThrough: isoDateSchema.optional(),
            disputed: z.boolean(),
        });
        Experian.summarySchema = z.object({
            totalDebt: currencySchema,
            openAccounts: z.number().int(),
            revolvingBalance: currencySchema,
            revolvingLimit: currencySchema,
            utilizationPct: z.number().optional(),
            monthlyPayments: currencySchema,
            onTimePct: z.number().optional(),
            delinquencies: z.object({
                late30: z.number().int(),
                late60: z.number().int(),
                late90Plus: z.number().int(),
            }),
            oldestOpenedOn: isoDateSchema.optional(),
            tradelines: z.object({
                total: z.number().int(),
                open: z.number().int(),
                closed: z.number().int(),
            }),
            mix: z.array(z.object({
                category: Experian.tradelineCategorySchema,
                balance: currencySchema,
                count: z.number().int(),
            })),
        });
        Experian.inquirySchema = z.object({
            date: isoDateSchema.optional(),
            inquirer: z.string(),
            creditType: z.string().optional(),
        });
        Experian.reportedAddressSchema = z.object({
            line1: z.string(),
            line2: z.string().optional(),
            firstReportedOn: isoDateSchema.optional(),
            lastReportedOn: isoDateSchema.optional(),
            current: z.boolean(),
        });
        Experian.employerSchema = z.object({
            name: z.string(),
            firstReportedOn: isoDateSchema.optional(),
            lastReportedOn: isoDateSchema.optional(),
        });
        Experian.identitySchema = z.object({
            akas: z.array(z.string()),
            addresses: z.array(Experian.reportedAddressSchema),
            employers: z.array(Experian.employerSchema),
            nameMatches: z.boolean().optional(),
            addressMatches: z.boolean().optional(),
        });
        Experian.detailSchema = z.object({
            scoreFactors: z.array(Experian.scoreFactorSchema),
            publicRecords: z.object({
                bankruptcies: z.array(Experian.bankruptcySchema),
                liens: z.array(Experian.lienSchema),
                judgments: z.array(Experian.judgmentSchema),
                collections: z.array(Experian.collectionSchema),
            }),
            summary: Experian.summarySchema,
            tradelines: z.array(Experian.tradelineSchema),
            inquiries: z.array(Experian.inquirySchema),
            identity: Experian.identitySchema,
        });
    })(Experian = Admin.Experian || (Admin.Experian = {}));
    const FailureReason = individualCreditReportFailureReasonSchema.enum;
    Admin.failureSchema = z.discriminatedUnion("reason", [
        z.object({ reason: z.literal(FailureReason.frozen) }),
        z.object({ reason: z.literal(FailureReason.not_found) }),
        z.object({ reason: z.literal(FailureReason.no_score) }),
        z.object({
            reason: z.literal(FailureReason.vendor_error),
            ...Vendor$1.vendorErrorSchema.shape,
        }),
        z.object({
            reason: z.literal(FailureReason.insufficient_data),
            missing: z.array(Vendor$1.creditReportInputFieldSchema).optional(),
        }),
        z.object({ reason: z.literal("unknown") }),
    ]);
    const failedOutcomeSchema = z.object({
        status: z.literal("failed"),
        failure: Admin.failureSchema,
    });
    const successOutcomeShape = {
        status: z.literal("success"),
        score: fico.optional(),
        bankruptcy: z.boolean().optional(),
        model: z.string().optional(),
        reusedUntil: isoDateTimeSchema,
    };
    const reportBaseShape = {
        id: individualCreditReportId,
        individualContactId: individualContactId,
        createdAt: isoDateTimeSchema,
        updatedAt: isoDateTimeSchema,
    };
    (function (Response) {
        Response.experianSchema = z.object({
            ...reportBaseShape,
            vendor: z.literal(individualCreditReportVendorSchema.enum.experian),
            outcome: z.discriminatedUnion("status", [
                z.object({
                    ...successOutcomeShape,
                    detail: Experian.detailSchema.optional(),
                }),
                failedOutcomeSchema,
            ]),
        });
        Response.meridianSchema = z.object({
            ...reportBaseShape,
            vendor: z.literal(individualCreditReportVendorSchema.enum.meridian),
            outcome: z.discriminatedUnion("status", [z.object(successOutcomeShape), failedOutcomeSchema]),
        });
        Response.schema = z.discriminatedUnion("vendor", [Response.experianSchema, Response.meridianSchema]);
    })(Admin.Response || (Admin.Response = {}));
})(Admin || (Admin = {}));
var OFAC;
(function (OFAC) {
    (function (Vendor) {
        Vendor.responseSchema = z.union([
            z.object({
                success: z.literal(false),
            }),
            z.object({
                success: z.literal(true),
                fileFound: z.literal(false),
            }),
            z.object({
                success: z.literal(true),
                fileFound: z.literal(true),
                report: OFAC$1.Vendor.reportSchema,
            }),
        ]);
    })(OFAC.Vendor || (OFAC.Vendor = {}));
    (function (Admin) {
        (function (Response) {
            Response.schema = z.object({
                id: individualOFACCheckId,
                success: z.boolean(),
                hit: z.boolean(),
                individualContactId: individualContactId,
                createdAt: isoDateTimeSchema,
                updatedAt: isoDateTimeSchema,
            });
        })(Admin.Response || (Admin.Response = {}));
    })(OFAC.Admin || (OFAC.Admin = {}));
})(OFAC || (OFAC = {}));

export { Admin, OFAC, Vendor };
